import { X, ArrowUpDown, Filter, Edit2, Trash2, Plus, Check, RefreshCw } from "lucide-react";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';

interface Column {
  field: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'badge' | 'id' | 'lookup' | 'tableLink';
  width?: number;
  visible?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  // Configurações para lookup (foreign key)
  lookupTable?: {
    tableId: string;
    keyField: string; // Campo na tabela referenciada (ex: 'id')
    displayField: string; // Campo a ser exibido (ex: 'status')
    data?: any[]; // Dados da tabela referenciada
  };
}

interface TableWidgetProps {
  title: string;
  data: any[];
  columns: Column[];
  pagination?: boolean;
  pageSize?: number;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  onEdit?: (row: any, index: number) => void;
  onDelete?: (row: any, index: number) => void;
  onAdd?: () => void;
  googleSheetsStatus?: {
    loading?: boolean;
    error?: string | null;
    connected?: boolean;
    recordCount?: number;
  };
  onSyncApi?: () => void;
  syncing?: boolean;
  lastSyncedAt?: string;
}

export default function TableWidget({
  title,
  data,
  columns,
  pagination = false,
  pageSize = 10,
  striped = true,
  hoverable = true,
  compact = false,
  onEdit,
  onDelete,
  onAdd,
  googleSheetsStatus,
  onSyncApi,
  syncing = false,
  lastSyncedAt,
}: TableWidgetProps) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);
  const [openFilterPopover, setOpenFilterPopover] = useState<string | null>(null);
  
  // Filtrar apenas colunas visíveis
  const visibleColumns = columns.filter(col => col.visible !== false);
  
  // Filtrar e ordenar dados
  const filteredData = useMemo(() => {
    if (!data) return [];
    
    let filtered = data.filter(row => {
      // Filtros por coluna
      for (const [field, filterValue] of Object.entries(columnFilters)) {
        if (filterValue) {
          const value = row[field];
          if (value == null) return false;
          if (!value.toString().toLowerCase().includes(filterValue.toLowerCase())) {
            return false;
          }
        }
      }
      
      return true;
    });

    // Aplicar ordenação
    if (sortConfig) {
      const sortColumn = columns.find((c) => c.field === sortConfig.field);
      const isNumeric = sortColumn?.type === 'number' || sortColumn?.type === 'id';

      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.field];
        const bValue = b[sortConfig.field];
        
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (isNumeric) {
          const aNum = parseFloat(String(aValue).replace(',', '.'));
          const bNum = parseFloat(String(bValue).replace(',', '.'));
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
          }
        }
        
        const aStr = aValue.toString().toLowerCase();
        const bStr = bValue.toString().toLowerCase();
        
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, columnFilters, sortConfig]);
  
  const clearAllFilters = () => {
    setColumnFilters({});
    setSortConfig(null);
  };
  
  const hasActiveFilters = Object.values(columnFilters).some(v => v) || sortConfig !== null;

  const handleSort = (field: string) => {
    setSortConfig(prev => {
      if (prev?.field === field) {
        return prev.direction === 'asc' ? { field, direction: 'desc' } : null;
      }
      return { field, direction: 'asc' };
    });
  };
  const renderCell = (value: any, column: Column, row: any) => {
    if (column.render) {
      return column.render(value, row);
    }

    // Lookup (Foreign Key)
    if (column.type === 'lookup' && column.lookupTable) {
      const lookupData = column.lookupTable.data || [];
      const lookupItem = lookupData.find(
        (item: any) => item[column.lookupTable!.keyField] == value
      );
      return lookupItem ? lookupItem[column.lookupTable.displayField] : value || 'N/A';
    }

    switch (column.type) {
      case 'id':
      case 'number':
        return new Intl.NumberFormat('pt-BR').format(value);
      case 'date':
        // Converte data do formato brasileiro (dd/mm/yyyy) ou ISO para exibição
        if (!value) return '';
        
        const stringValue = value.toString().trim();
        let dateToFormat: Date;
        
        // Verifica se está no formato brasileiro (dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy)
        const brDatePattern = /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}$/;
        
        if (brDatePattern.test(stringValue)) {
          // Formato brasileiro: converte para Date
          const parts = stringValue.split(/[\/\-\.]/);
          const day = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Mês começa em 0
          const year = parseInt(parts[2]);
          dateToFormat = new Date(year, month, day);
        } else {
          // Formato ISO ou outro formato aceito pelo Date
          dateToFormat = new Date(value);
        }
        
        return !isNaN(dateToFormat.getTime()) 
          ? dateToFormat.toLocaleDateString('pt-BR')
          : value;
      case 'badge':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
            {value}
          </span>
        );
      case 'tableLink': {
        if (!value) return null;
        const linkData = typeof value === 'object' ? value : {};
        if (!linkData.name) return String(value || '');
        if (linkData.pageSlug) {
          return (
            <a
              href={`/pages/${linkData.pageSlug}`}
              className="text-primary underline hover:text-primary/80 font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {linkData.name}
            </a>
          );
        }
        return <span>{linkData.name}</span>;
      }
      default:
        return value;
    }
  };

  return (
    <Card className="h-full flex flex-col overflow-hidden">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <CardTitle className="truncate">{title}</CardTitle>
            
            {/* Google Sheets Status Badge */}
            {googleSheetsStatus && (
              <>
                {googleSheetsStatus.loading && (
                  <Badge variant="secondary" className="gap-1">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Syncing
                  </Badge>
                )}
                {googleSheetsStatus.error && (
                  <Badge variant="destructive" className="gap-1" title={googleSheetsStatus.error}>
                    <X className="w-3 h-3" />
                    Error
                  </Badge>
                )}
                {googleSheetsStatus.connected && !googleSheetsStatus.loading && !googleSheetsStatus.error && (
                  <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" title={`${googleSheetsStatus.recordCount} registros`}>
                    <Check className="w-3 h-3" />
                    Synced
                  </Badge>
                )}
              </>
            )}
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            {onSyncApi && (
              <Button
                variant="outline"
                size="sm"
                onClick={onSyncApi}
                disabled={syncing}
                title={
                  lastSyncedAt
                    ? `Última sincronização: ${new Date(lastSyncedAt).toLocaleString("pt-BR")}`
                    : "Buscar dados da API e salvar no banco"
                }
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Sincronizando..." : "Sincronizar"}
              </Button>
            )}
            {onAdd && (
              <Button
                variant="default"
                size="sm"
                onClick={onAdd}
              >
                <Plus className="w-4 h-4 mr-1" />
                Adicionar Linha
              </Button>
            )}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
              >
                <X className="w-4 h-4 mr-1" />
                Limpar filtros
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto p-0">

        <div className="w-full h-full overflow-x-auto">
          <table className="w-full divide-y">
          <thead className="bg-muted/50">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.field}
                  className="px-6 py-3 text-left"
                  style={{ width: column.width }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {column.label}
                      {sortConfig?.field === column.field && (
                        <span className="ml-1 text-primary">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleSort(column.field)}
                        className="p-1 hover:bg-muted rounded transition-colors"
                        title="Ordenar"
                      >
                        <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                      </button>
                      
                      <Popover 
                        open={openFilterPopover === column.field} 
                        onOpenChange={(open: boolean) => setOpenFilterPopover(open ? column.field : null)}
                      >
                        <PopoverTrigger asChild>
                          <button 
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title="Filtrar"
                          >
                            <Filter className={`w-3.5 h-3.5 ${columnFilters[column.field] ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64" align="start">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Filtrar {column.label}</label>
                            <Input
                              type="text"
                              placeholder="Digite para filtrar..."
                              value={columnFilters[column.field] || ''}
                              onChange={(e) => setColumnFilters(prev => ({
                                ...prev,
                                [column.field]: e.target.value
                              }))}
                              className="h-8"
                            />
                            {columnFilters[column.field] && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setColumnFilters(prev => {
                                    const newFilters = { ...prev };
                                    delete newFilters[column.field];
                                    return newFilters;
                                  });
                                  setOpenFilterPopover(null);
                                }}
                                className="w-full"
                              >
                                Limpar filtro
                              </Button>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </th>
              ))}
              <th className="px-6 py-3 text-left">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Ações
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y">
            {filteredData && filteredData.length > 0 ? (
              filteredData.slice(0, pagination ? pageSize : undefined).map((row, idx) => (
                <tr 
                  key={idx} 
                  className={`
                    group/row
                    ${hoverable ? 'hover:bg-muted/50' : ''}
                    ${striped && idx % 2 === 1 ? 'bg-muted/30' : ''}
                  `}
                >
                  {visibleColumns.map((column) => (
                    <td 
                      key={column.field} 
                      className={`px-6 ${compact ? 'py-2' : 'py-4'}`}
                    >
                      <div className="max-w-xs truncate">
                        {renderCell(row[column.field], column, row)}
                      </div>
                    </td>
                  ))}
                  <td className={`px-6 whitespace-nowrap ${compact ? 'py-2' : 'py-4'}`}>
                    <div className="flex items-center gap-2 invisible group-hover/row:visible">
                      {onEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(row, idx);
                          }}
                          className="p-1.5 hover:bg-muted rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(row, idx);
                          }}
                          className="p-1.5 hover:bg-destructive/10 rounded transition-colors"
                          title="Deletar"
                        >
                          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={visibleColumns.length + 1}
                  className="px-6 py-8 text-center text-muted-foreground"
                >
                  {hasActiveFilters ? 'Nenhum resultado encontrado' : 'Nenhum dado disponível'}
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
      </CardContent>

      {pagination && filteredData.length > pageSize && (
        <CardFooter className="text-sm text-muted-foreground">
          Mostrando {Math.min(pageSize, filteredData.length)} de {filteredData.length} registros
          {hasActiveFilters && data.length !== filteredData.length && (
            <span className="ml-2">
              (filtrado de {data.length} total)
            </span>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
